import { FormLayout } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormUi } from './formUi';
import { FormData, getLocalizedSchema } from './schema';
import { useRequestResetPasswordFormContext } from '../context';
import { get, isString } from 'lodash';

type Props = {
  loading: boolean;
  onSubmit: SubmitHandler<FormData>;
};

enum ERROR_CODES {
  CODE_MISMATCH = 'CodeMismatchException',
  LIMIT_EXCEEDED = 'LimitExceededException',
}

export const ResetPasswordForm = ({ loading, onSubmit }: Props): JSX.Element => {
  const { t } = useTranslation();

  const { formClassname } = useRequestResetPasswordFormContext();

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
    disabled: loading,
  });
  const { handleSubmit, setError } = methods;

  const onSubmitReset = async (d: FormData) => {
    try {
      await onSubmit(d);
    } catch (e) {
      let err: string | undefined = get(e, 'code');
      if (!isString(err)) {
        err = ERROR_CODES.CODE_MISMATCH;
      }
      const errorCode = Object.values(ERROR_CODES).includes(err as ERROR_CODES) ? err : ERROR_CODES.CODE_MISMATCH;

      switch (errorCode as ERROR_CODES) {
        case ERROR_CODES.CODE_MISMATCH:
          setError('code', {
            message: t(`login.passwordReset.errors.${errorCode}`),
          });
          break;
        case ERROR_CODES.LIMIT_EXCEEDED:
          setError('root', {
            message: t(`login.passwordReset.errors.${errorCode}`),
          });
          break;
      }
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <FormLayout onSubmit={handleSubmit(onSubmitReset)} className={formClassname}>
          <FormUi isLoading={loading} />
        </FormLayout>
      </FormProvider>
    </>
  );
};
